import React, { useState, useCallback, useEffect } from 'react';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
/* ─── TOKENS — exact match to DevTools.jsx ───────────────────────────── */
const T = {
  bg:      '#0a0c10',
  bg2:     '#0e1117',
  bg3:     '#13171f',
  surface: '#1a1f2b',
  border:  'rgba(255,255,255,.07)',
  borderHi:'rgba(255,255,255,.12)',
  white:   '#e8edf5',
  muted:   'rgba(255,255,255,0.50)',
  dim:     'rgba(255,255,255,0.50)',
  green:   '#3dd68c',
  red:     '#ff5f57',
  yellow:  '#fbbf24',
  blue:    '#4d9fff',
  purple:  '#b78cff',
  orange:  '#ff9f43',
  cyan:    '#00d4ff',
  mono:    '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
  sans:    '"DM Sans","Outfit","system-ui",sans-serif',
};

/* ─── SHARED ATOMS — identical to DevTools.jsx ───────────────────────── */
const EDITOR_STYLE = {
  width: '100%',
  minHeight: 220,
  padding: '14px 16px',
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  color: T.white,
  fontSize: 12.5,
  fontFamily: T.mono,
  lineHeight: 1.65,
  resize: 'vertical',
  outline: 'none',
  transition: 'border-color .15s',
  boxSizing: 'border-box',
};

function Label({ children }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 700, letterSpacing: '.1em',
      textTransform: 'uppercase', color: T.dim,
      fontFamily: T.sans, marginBottom: 6,
    }}>{children}</div>
  );
}

function IconBtn({ onClick, children, danger, active }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 12px', borderRadius: 5,
        border: `1px solid ${hover || active ? (danger ? T.red+'50' : T.borderHi) : T.border}`,
        background: active ? T.blue+'18' : hover ? (danger ? T.red+'12' : T.surface) : 'transparent',
        color: active ? T.blue : hover ? (danger ? T.red : T.white) : T.muted,
        fontSize: 11.5, fontFamily: T.sans, fontWeight: active ? 600 : 400,
        cursor: 'pointer', transition: 'all .12s',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        whiteSpace: 'nowrap',
      }}>
      {children}
    </button>
  );
}

function Pill({ children, active, onClick, color = T.blue }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 6,
      border: `1px solid ${active ? color+'60' : T.border}`,
      background: active ? color+'18' : 'transparent',
      color: active ? color : T.muted,
      fontSize: 12.5, fontWeight: 600, fontFamily: T.sans,
      cursor: 'pointer', letterSpacing: '.03em', transition: 'all .15s',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function StatChip({ label, value }) {
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 7, flex: 1, minWidth: 80,
      background: T.surface, border: `1px solid ${T.border}`, textAlign: 'center',
    }}>
      <div style={{ fontSize: 11, color: T.dim, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.mono, color: T.cyan }}>{value}</div>
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconBtn onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
      {copied ? '✓ Copied' : '⧉ Copy'}
    </IconBtn>
  );
}

/* ─── TOOLS ──────────────────────────────────────────────────────────── */

function WordCounter() {
  const [text, setText] = useState('');
  const words     = text.trim() ? text.trim().split(/\s+/).length : 0;
  const chars     = text.length;
  const noSpaces  = text.replace(/\s/g, '').length;
  const lines     = text ? text.split('\n').length : 0;
  const sentences = text.trim() ? text.split(/[.!?]+/).filter(Boolean).length : 0;
  const readMin   = Math.max(1, Math.round(words / 200));
  return (
    <div>
      <Label>Input Text</Label>
      <textarea style={EDITOR_STYLE} value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Paste or type your text here…"/>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:12 }}>
        {[['Words',words],['Characters',chars],['No spaces',noSpaces],['Lines',lines],['Sentences',sentences],['Read time',readMin+'min']].map(([l,v]) => (
          <StatChip key={l} label={l} value={v}/>
        ))}
      </div>
    </div>
  );
}

function CaseConverter() {
  const [text, setText] = useState('');
  const [result, setResult] = useState('');
  const cases = [
    { label:'UPPER',      fn: t => t.toUpperCase()                                                              },
    { label:'lower',      fn: t => t.toLowerCase()                                                              },
    { label:'Title',      fn: t => t.replace(/\w\S*/g, w => w[0].toUpperCase()+w.slice(1).toLowerCase())        },
    { label:'Sentence',   fn: t => t.charAt(0).toUpperCase()+t.slice(1).toLowerCase()                           },
    { label:'camelCase',  fn: t => t.toLowerCase().replace(/[^a-z0-9]+(.)/gi, (_,c) => c.toUpperCase())         },
    { label:'snake_case', fn: t => t.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'')               },
    { label:'kebab-case', fn: t => t.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')               },
    { label:'PascalCase', fn: t => t.replace(/\w\S*/g, w => w[0].toUpperCase()+w.slice(1))                     },
  ];
  return (
    <div>
      <Label>Input</Label>
      <textarea style={{ ...EDITOR_STYLE, minHeight:120 }} value={text}
        onChange={e => setText(e.target.value)} placeholder="Enter text to convert…"/>
      <div style={{ display:'flex', flexWrap:'wrap', gap:5, margin:'10px 0' }}>
        {cases.map(c => (
          <Pill key={c.label} onClick={() => setResult(c.fn(text))}>{c.label}</Pill>
        ))}
      </div>
      {result && (
        <>
          <Label>Output</Label>
          <textarea style={{ ...EDITOR_STYLE, minHeight:80, background: T.bg3 }} value={result} readOnly/>
          <div style={{ marginTop:6 }}><CopyBtn value={result}/></div>
        </>
      )}
    </div>
  );
}

function DedupeTool() {
  const [text, setText] = useState('');
  const [trim, setTrim] = useState(true);
  const [cs,   setCs]   = useState(true);
  const lines   = text.split('\n');
  const seen    = new Set();
  const deduped = lines.filter(line => {
    const key = cs ? (trim ? line.trim() : line) : (trim ? line.trim() : line).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key); return true;
  }).join('\n');
  const removed = lines.length - deduped.split('\n').filter(Boolean).length;
  return (
    <div>
      <div style={{ display:'flex', gap:14, marginBottom:10, flexWrap:'wrap' }}>
        {[['Trim whitespace', trim, setTrim], ['Case-sensitive', cs, setCs]].map(([l,v,s]) => (
          <label key={l} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12.5, color:T.muted }}>
            <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} style={{ accentColor:T.cyan }}/>{l}
          </label>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div>
          <Label>Input — {lines.filter(Boolean).length} lines</Label>
          <textarea style={EDITOR_STYLE} value={text} onChange={e => setText(e.target.value)} placeholder="Paste lines here…"/>
        </div>
        <div>
          <Label>Output — {removed} duplicates removed</Label>
          <textarea style={{ ...EDITOR_STYLE, background:T.bg3 }} value={deduped} readOnly/>
        </div>
      </div>
      {deduped && <div style={{ marginTop:6 }}><CopyBtn value={deduped}/></div>}
    </div>
  );
}

const LOREM_BANK = "lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua ut enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt in culpa qui officia deserunt mollit anim id est laborum".split(' ');
const rw  = () => LOREM_BANK[Math.floor(Math.random()*LOREM_BANK.length)];
const rSentence = () => { const n=8+Math.floor(Math.random()*10); return Array.from({length:n}, (_,i) => i===0?rw().charAt(0).toUpperCase()+rw().slice(1):rw()).join(' ')+'.'; };
const rPara     = () => Array.from({length:4+Math.floor(Math.random()*3)}, rSentence).join(' ');

function LoremIpsum() {
  const [type,   setType]   = useState('paragraphs');
  const [count,  setCount]  = useState(3);
  const [output, setOutput] = useState('');
  const generate = () => {
    if (type==='words')      setOutput(Array.from({length:count}, rw).join(' '));
    if (type==='sentences')  setOutput(Array.from({length:count}, rSentence).join(' '));
    if (type==='paragraphs') setOutput(Array.from({length:count}, rPara).join('\n\n'));
  };
  return (
    <div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:14 }}>
        <select value={type} onChange={e => setType(e.target.value)} style={{
          padding:'7px 10px', borderRadius:7, background:T.surface,
          border:`1px solid ${T.border}`, color:T.white, fontSize:13, fontFamily:T.sans,
        }}>
          <option value="words">Words</option>
          <option value="sentences">Sentences</option>
          <option value="paragraphs">Paragraphs</option>
        </select>
        <input type="number" min={1} max={20} value={count}
          onChange={e => setCount(+e.target.value)}
          style={{ width:64, padding:'7px 10px', borderRadius:7, background:T.surface, border:`1px solid ${T.border}`, color:T.white, fontSize:13, fontFamily:T.mono, outline:'none' }}
        />
        <Pill active onClick={generate} color={T.cyan}>↺ Generate</Pill>
      </div>
      {output && (
        <>
          <Label>Output</Label>
          <textarea style={{ ...EDITOR_STYLE, minHeight:200, background:T.bg3 }} value={output} readOnly/>
          <div style={{ marginTop:6 }}><CopyBtn value={output}/></div>
        </>
      )}
    </div>
  );
}

function toHtml(text) {
  return text
    .replace(/```[\w]*\n([\s\S]*?)```/g, `<pre style="background:${T.surface};padding:10px 12px;border-radius:6px;overflow:auto;font-size:12px"><code>$1</code></pre>`)
    .replace(/`([^`]+)`/g, `<code style="background:${T.surface};padding:1px 5px;border-radius:3px;font-family:monospace;font-size:12px">$1</code>`)
    .replace(/^### (.+)$/gm, `<h3 style="font-size:15px;font-weight:600;margin:14px 0 5px;color:${T.white}">$1</h3>`)
    .replace(/^## (.+)$/gm,  `<h2 style="font-size:17px;font-weight:700;margin:16px 0 7px;color:${T.white}">$1</h2>`)
    .replace(/^# (.+)$/gm,   `<h1 style="font-size:20px;font-weight:800;margin:16px 0 9px;color:${T.white}">$1</h1>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, `<a href="$2" style="color:${T.cyan}">$1</a>`)
    .replace(/^> (.+)$/gm, `<blockquote style="border-left:2px solid ${T.blue};padding-left:12px;color:${T.muted};margin:8px 0">$1</blockquote>`)
    .replace(/^- (.+)$/gm, '<li style="margin:3px 0">$1</li>')
    .replace(/(<li.*<\/li>)/s, '<ul style="padding-left:18px;margin:8px 0">$&</ul>')
    .replace(/^(?!<[h1-6|pre|ul|blockquote|li])(.+)$/gm, `<p style="margin:5px 0;line-height:1.65;color:${T.muted}">$1</p>`);
}

function MarkdownPreview() {
  const [md, setMd] = useState('# Hello\n\nThis is **bold** and _italic_.\n\n- Item one\n- Item two\n\n```js\nconsole.log("Hello")\n```\n\n> A blockquote.');
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <div>
        <Label>Markdown</Label>
        <textarea style={EDITOR_STYLE} value={md} onChange={e => setMd(e.target.value)}/>
      </div>
      <div>
        <Label>Preview</Label>
        <div style={{
          padding:'14px 16px', minHeight:220,
          background:T.bg, border:`1px solid ${T.border}`, borderRadius:8,
          fontSize:13.5, lineHeight:1.7, overflowY:'auto',
        }} dangerouslySetInnerHTML={{ __html: toHtml(md) }}/>
      </div>
    </div>
  );
}

function HtmlEncoder() {
  const [input, setInput] = useState('');
  const [mode,  setMode]  = useState('encode');
  const encode = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  const decode = s => s.replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&#39;/g,"'");
  const result = input ? (mode==='encode' ? encode(input) : decode(input)) : '';
  return (
    <div>
      <div style={{ display:'flex', gap:6, marginBottom:12 }}>
        <Pill active={mode==='encode'} onClick={() => setMode('encode')}>Encode</Pill>
        <Pill active={mode==='decode'} onClick={() => setMode('decode')}>Decode</Pill>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div>
          <Label>Input</Label>
          <textarea style={EDITOR_STYLE} value={input} onChange={e => setInput(e.target.value)}
            placeholder={mode==='encode' ? '<div class="hello">World</div>' : '&lt;div&gt;World&lt;/div&gt;'}/>
        </div>
        <div>
          <Label>Output</Label>
          <textarea style={{ ...EDITOR_STYLE, background:T.bg3 }} value={result} readOnly/>
        </div>
      </div>
      {result && <div style={{ marginTop:6 }}><CopyBtn value={result}/></div>}
    </div>
  );
}

function HtmlToMarkdown() {
  const [html, setHtml] = useState('');
  const toMd = h => h
    .replace(/<h([1-3])[^>]*>(.*?)<\/h\1>/gi, (_, n, t) => '#'.repeat(+n)+' '+t+'\n')
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi,'**$1**').replace(/<b[^>]*>(.*?)<\/b>/gi,'**$1**')
    .replace(/<em[^>]*>(.*?)<\/em>/gi,'_$1_').replace(/<i[^>]*>(.*?)<\/i>/gi,'_$1_')
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi,'[$2]($1)')
    .replace(/<li[^>]*>(.*?)<\/li>/gi,'- $1\n').replace(/<(ul|ol)[^>]*>|<\/(ul|ol)>/gi,'\n')
    .replace(/<br\s*\/?>/gi,'\n').replace(/<p[^>]*>(.*?)<\/p>/gi,'$1\n\n')
    .replace(/<[^>]+>/g,'').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>')
    .replace(/\n{3,}/g,'\n\n').trim();
  const result = html ? toMd(html) : '';
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div>
          <Label>HTML Input</Label>
          <textarea style={EDITOR_STYLE} value={html} onChange={e => setHtml(e.target.value)}
            placeholder="<h1>Title</h1><p>Hello <strong>world</strong></p>"/>
        </div>
        <div>
          <Label>Markdown Output</Label>
          <textarea style={{ ...EDITOR_STYLE, background:T.bg3 }} value={result} readOnly/>
        </div>
      </div>
      {result && <div style={{ marginTop:6 }}><CopyBtn value={result}/></div>}
    </div>
  );
}

/* ─── TOOL REGISTRY ─────────────────────────────────────────────────── */
const TOOLS = [
  { id:'wordcount', label:'Word Counter',        subtitle:'Words, chars, sentences', component:WordCounter  },
  { id:'case',      label:'Case Converter',      subtitle:'UPPER, lower, camelCase', component:CaseConverter},
  { id:'dedupe',    label:'Remove Duplicates',   subtitle:'Deduplicate lines',       component:DedupeTool   },
  { id:'lorem',     label:'Lorem Ipsum',         subtitle:'Generate placeholder text',component:LoremIpsum  },
  { id:'markdown',  label:'Markdown Preview',    subtitle:'Live render',             component:MarkdownPreview},
  { id:'htmlenc',   label:'HTML Encode/Decode',  subtitle:'Escape HTML entities',    component:HtmlEncoder  },
  { id:'htmltomd',  label:'HTML → Markdown',     subtitle:'Convert HTML to MD',      component:HtmlToMarkdown},
];


const textToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free Text Tools — Tylented",
  "url":         "https://arhat.info/tools/text",
  "description": "Free everyday text tools: word counter with reading time, case converter (UPPER, lower, Title, Sentence, camelCase, snake_case, kebab-case, PascalCase), remove duplicate lines, lorem ipsum generator, markdown live preview, HTML encoder/decoder, HTML to Markdown converter.",
  "applicationCategory": "UtilitiesApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "Word Counter — words, characters, sentences, lines, reading time",
    "Case Converter — 8 formats including camelCase, snake_case, kebab-case",
    "Remove Duplicate Lines — case-sensitive and case-insensitive",
    "Lorem Ipsum Generator — words, sentences, or paragraphs",
    "Markdown Live Preview",
    "HTML Encoder and Decoder",
    "HTML to Markdown Converter"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};
 
const textToolsFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "How do I count words in text online for free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Paste your text into the Word Counter at arhat.info/tools/text. It instantly shows word count, character count (with and without spaces), sentence count, line count, and estimated reading time at 200 words per minute. No sign-up, no copy limits."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I convert text to camelCase, snake_case or kebab-case?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the Case Converter at arhat.info/tools/text. Paste your text and click the format you need: UPPER CASE, lower case, Title Case, Sentence case, camelCase, snake_case, kebab-case, or PascalCase. Converted text appears instantly and can be copied with one click."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I remove duplicate lines from a list?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Use the Remove Duplicates tool at arhat.info/tools/text. Paste your lines, choose case-sensitive or case-insensitive matching, and toggle whitespace trimming. Duplicate lines are removed instantly and the count of removed duplicates is shown."
      }
    }
  ]
};

const Mapping = {
  'counter': 'wordcount',
  'case':    'case',
  'dedupe':  'dedupe',
  'lorem':   'lorem',
  'markdown':'markdown',
  'htmlenc': 'htmlenc',
  'htmltomd':'htmltomd',
  'word-counter': 'wordcount',
  'case-converter':'case',
  'remove-duplicates':'dedupe',
  'lorem-ipsum':'lorem',
  'markdown-preview':'markdown',
  'html-encoder':'htmlenc',
  'html-to-markdown':'htmltomd',
  
}

const TEXT_FAQS = {
  wordcount: [
    {
      q: 'How does the word counter count words?',
      a: 'Words are counted by splitting the text on whitespace and filtering out empty strings. Hyphenated words like "well-known" count as one word. Numbers, punctuation-only tokens, and single characters each count as one word.',
    },
    {
      q: 'How is reading time calculated?',
      a: 'Reading time is estimated at 200 words per minute — the average adult silent reading speed. Academic or technical content is typically read at 150–180 wpm; casual content at 200–250 wpm. The estimate is a useful guideline, not a precise measurement.',
    },
    {
      q: 'What is the difference between characters with and without spaces?',
      a: 'Characters with spaces counts every character including spaces, tabs, and newlines. Characters without spaces counts only non-whitespace characters. Both counts are useful — Twitter\'s character limit counts spaces; some publishing tools don\'t.',
    },
    {
      q: 'How are sentences counted?',
      a: 'Sentences are counted by splitting on sentence-ending punctuation (., !, ?). This is an approximation — abbreviations like "Dr." or "e.g." may be counted as sentence ends. The count is most accurate for standard prose.',
    },
    {
      q: 'What is the Flesch reading ease score?',
      a: 'The Flesch reading ease score rates text on a 0–100 scale. 90–100 is very easy (5th grade level). 60–70 is standard (8th–9th grade). 0–30 is very difficult (professional/academic). Shorter sentences and simpler words produce higher (easier) scores.',
    },
  ],

  caseconvert: [
    {
      q: 'What is camelCase?',
      a: 'camelCase joins words together with no spaces, capitalising the first letter of each word except the first. Example: "hello world" becomes "helloWorld". Used in JavaScript variable names, JSON keys, and most programming languages.',
    },
    {
      q: 'What is the difference between camelCase and PascalCase?',
      a: 'camelCase starts with a lowercase letter: "helloWorld". PascalCase (also called UpperCamelCase) starts with an uppercase letter: "HelloWorld". PascalCase is used for class names in most languages; camelCase for variable and function names.',
    },
    {
      q: 'What is snake_case used for?',
      a: 'snake_case uses underscores between words with all lowercase letters. It\'s the standard in Python (variables, functions, file names), Ruby, and SQL column names. Example: "hello world" becomes "hello_world".',
    },
    {
      q: 'What is kebab-case?',
      a: 'kebab-case uses hyphens between words, all lowercase. It\'s used in CSS class names, HTML attributes, URL slugs, and file names. Example: "hello world" becomes "hello-world". Also called lisp-case or spinal-case.',
    },
    {
      q: 'What is SCREAMING_SNAKE_CASE used for?',
      a: 'SCREAMING_SNAKE_CASE (also called UPPER_SNAKE_CASE) uses underscores and all uppercase letters. It\'s the convention for constants in most languages: Python, Java, C, JavaScript. Example: MAX_RETRY_COUNT.',
    },
    {
      q: 'What is Title Case?',
      a: 'Title Case capitalises the first letter of each word. "hello world" becomes "Hello World". Used for headings, titles, and proper nouns. Strict title case has additional rules about not capitalising articles (a, an, the) and prepositions, but this tool applies simple title case.',
    },
  ],

  duplicates: [
    {
      q: 'What does the remove duplicates tool do?',
      a: 'It takes a block of text, splits it into lines, removes any lines that appear more than once, and returns only the unique lines. Useful for cleaning up lists, log files, keyword lists, or any line-separated data with repeated entries.',
    },
    {
      q: 'Is the duplicate check case-sensitive?',
      a: 'By default yes — "Hello" and "hello" are treated as different lines. Most tools offer a case-insensitive mode where both would be considered duplicates and only one kept. Check the option toggle if available.',
    },
    {
      q: 'Does it preserve the original order?',
      a: 'Yes — the first occurrence of each line is kept and its position is preserved. Subsequent duplicates are removed. The output is not sorted alphabetically unless you specifically sort it afterwards.',
    },
    {
      q: 'Can I use this to clean up a keyword list?',
      a: 'Yes — paste your keywords one per line, run the duplicate remover, and you\'ll get a deduplicated list. For comma-separated keywords, you\'d need to convert them to one-per-line first (use the case converter or a find/replace).',
    },
  ],

  lorem: [
    {
      q: 'What is Lorem Ipsum?',
      a: 'Lorem Ipsum is placeholder text derived from Cicero\'s "de Finibus Bonorum et Malorum" (45 BC). It has been the standard dummy text for typesetting and design since the 1500s. It\'s used to fill layouts when the real content isn\'t available yet.',
    },
    {
      q: 'Why is Lorem Ipsum used instead of real text?',
      a: 'Placeholder text lets reviewers focus on layout, typography, and design rather than getting distracted by actual content. It has a natural distribution of letters and word lengths that looks like real text from a distance.',
    },
    {
      q: 'Can I generate Lorem Ipsum by word count, sentence count, or paragraph count?',
      a: 'Yes — this generator supports all three modes. Word count is useful for fitting specific character limits. Sentence count works well for UI elements. Paragraph count is best for testing full content layouts.',
    },
    {
      q: 'Is Lorem Ipsum Latin?',
      a: 'It\'s derived from Latin but the standard Lorem Ipsum text is deliberately scrambled and not meaningful Latin. The original Cicero passage discusses the philosophy of pleasure and pain, but the Lorem Ipsum version is intentionally nonsensical.',
    },
  ],

  markdown: [
    {
      q: 'What is Markdown?',
      a: 'Markdown is a lightweight markup language that uses plain-text formatting syntax. You write **bold**, *italic*, # headings, and - lists in plain text, and it renders as formatted HTML. Created by John Gruber in 2004.',
    },
    {
      q: 'What Markdown flavours does the preview support?',
      a: 'The preview renders CommonMark, which is the standardised Markdown specification. It supports headings, bold, italic, links, images, code blocks, inline code, blockquotes, ordered and unordered lists, and tables (GitHub Flavoured Markdown extension).',
    },
    {
      q: 'How do I create a table in Markdown?',
      a: 'Use pipes and hyphens:\n| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\nThe hyphens in the second row separate the header from the body. Column alignment can be controlled with colons: |:---| for left, |:---:| for center, |---:| for right.',
    },
    {
      q: 'How do I add a code block in Markdown?',
      a: 'Use triple backticks before and after the code. Add the language name after the opening backticks for syntax highlighting: ```javascript. For inline code, wrap with single backticks: `const x = 1`.',
    },
    {
      q: 'Where is Markdown used?',
      a: 'GitHub READMEs and pull requests, Reddit posts, Stack Overflow answers, Notion, Obsidian, Discord messages, Jekyll and Hugo static sites, Jupyter notebooks, and most modern documentation platforms.',
    },
  ],

  htmlencode: [
    {
      q: 'What is HTML encoding?',
      a: 'HTML encoding replaces special characters with their HTML entity equivalents so they display correctly in a browser without being interpreted as HTML tags. For example, < becomes &lt; and & becomes &amp;.',
    },
    {
      q: 'When do I need to HTML encode text?',
      a: 'When you want to display HTML code as visible text rather than rendered markup. When including user input in HTML to prevent XSS attacks. When embedding special characters like <, >, &, or " in HTML attributes or content.',
    },
    {
      q: 'What is the difference between HTML encoding and URL encoding?',
      a: 'HTML encoding converts characters to HTML entities (&amp;, &lt;, etc.) for safe display in HTML. URL encoding converts characters to percent-encoded sequences (%26, %3C, etc.) for safe inclusion in URLs. They are used in different contexts.',
    },
    {
      q: 'What is an HTML entity?',
      a: 'An HTML entity is a string starting with & and ending with ; that represents a character. &lt; represents <, &gt; represents >, &amp; represents &, &quot; represents ", and &nbsp; represents a non-breaking space. Named entities exist for hundreds of special characters.',
    },
  ],

  htmltomd: [
    {
      q: 'What does HTML to Markdown conversion do?',
      a: 'It takes HTML markup and converts it to equivalent Markdown syntax. <strong> becomes **bold**, <em> becomes *italic*, <h1> becomes # Heading, <a href="..."> becomes [text](url), and so on. Useful for migrating content from CMS to Markdown-based systems.',
    },
    {
      q: 'Does it preserve all HTML formatting?',
      a: 'Common formatting elements convert cleanly: headings, bold, italic, links, images, lists, blockquotes, and code blocks. Complex layouts with tables, nested divs, CSS styles, and custom classes may not convert perfectly — Markdown has no equivalent for many HTML features.',
    },
    {
      q: 'What should I do with HTML that doesn\'t convert?',
      a: 'Markdown allows raw HTML embedded in it. Most Markdown parsers pass through HTML tags they don\'t understand. You can leave complex HTML sections as raw HTML within your Markdown document and they will render correctly.',
    },
    {
      q: 'Can I convert a whole web page to Markdown?',
      a: 'You can paste the page\'s HTML source and convert it, but you\'ll get a lot of navigation, footer, and sidebar content mixed in. For extracting just the article content, browser extensions like "Copy as Markdown" do a better job of identifying the main content area.',
    },
  ],
};

const GENERIC_TEXT_FAQS = [
  {
    q: 'Are these text tools free?',
    a: 'Yes — all text tools are completely free with no account required.',
  },
  {
    q: 'Does my text get sent to a server?',
    a: 'No — all text processing happens entirely in your browser. Your text never leaves your device.',
  },
  {
    q: 'Is there a text length limit?',
    a: 'There is no hard limit imposed by the tools. Very large documents (millions of characters) may be slow in the browser due to JavaScript processing limits.',
  },
];

function TextFAQSection({ active }) {
  const [open, setOpen] = useState(null);
  const faqs = TEXT_FAQS[active] || GENERIC_TEXT_FAQS;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border:     `1px solid ${isOpen ? T.blue+'55' : T.border}`,
            borderRadius:10,
            background:  isOpen ? T.blue+'08' : T.bg2,
            overflow:   'hidden',
            transition: 'all .2s',
          }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:'100%', display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                background:'transparent', border:'none',
                cursor:'pointer', textAlign:'left', gap:12,
              }}
            >
              <span style={{
                fontSize:13.5, fontWeight:600,
                color: isOpen ? T.blue : T.white,
                lineHeight:1.4, transition:'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:0, width:20, height:20, borderRadius:'50%',
                border:`1px solid ${isOpen ? T.blue : T.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, color: isOpen ? T.blue : T.muted,
                transition:'all .2s',
                transform: isOpen ? 'rotate(45deg)' : 'none',
              }}>+</span>
            </button>

            {isOpen && (
              <div style={{
                padding:'12px 16px 14px',
                fontSize:13, color:'rgba(220,230,250,.65)',
                lineHeight:1.75,
                borderTop:`1px solid ${T.blue}20`,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── MAIN — matches DevTools.jsx layout exactly ────────────────────── */
export default function TextTools() {
    const navigate = useNavigate();
  
    const hostname = window.location.hostname;
  
  // app.dev.example.com => ["app", "dev", "example", "com"]
  const parts = hostname.split(".");
  
  const firstSubdomain =
    parts.length > 2 ? parts[0] : null;
  
  console.log(firstSubdomain);
  
  const path = window.location.pathname;
  
  // "/tools/useragent/"
  const match = path.match(/^\/tools\/text\/([^/]+)/);
  
  const toolName = match ? match[1] : null;          
      
    const [active, setActive] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'wordcount');
      const Tool = TOOLS.find(t => t.id === active)?.component;
  const meta = TOOLS.find(t => t.id === active);
useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [textToolsSchema, textToolsFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);
  return (
    <>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        * { box-sizing:border-box; margin:0; padding:0; }
        ::placeholder { color:${T.dim} !important; }
        textarea:focus, input:focus, select:focus {
          border-color: rgba(77,159,255,.4) !important; outline:none;
        }
        select option { background: ${T.bg2}; color: ${T.white}; }
        /* ── Mobile ──────────────────────────────── */
        @media (max-width:640px) {
          .tt-grid-2 { grid-template-columns:1fr !important; }
          .tt-tabs   { flex-wrap:wrap; }
          .tt-tab    { flex:1 1 auto; min-width:140px; }
          .tt-panel  { padding:14px !important; }
          .tt-sidebar{ display:none !important; }
          .tt-mobile-nav { display:flex !important; }
        }
        @media (min-width:641px) {
          .tt-mobile-nav { display:none !important; }
        }
      `}</style>
 <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <SEO
  title="Word Counter, Case Converter, Lorem Ipsum & Text Tools — Free | Tylented"
  description="Free everyday text tools: count words and reading time, convert text to camelCase or snake_case, remove duplicate lines, generate lorem ipsum placeholder text, preview Markdown, encode HTML, convert HTML to Markdown. Instant results, no sign-up."
  keywords="word counter online free, character counter, reading time calculator, case converter online, camelcase converter, snake case converter, kebab case, remove duplicate lines tool, lorem ipsum generator, markdown preview online, html encoder decoder, html to markdown converter, text tools free online"
  canonical="https://arhat.info/tools/text"
  og={{
    title:       "Free Word Counter, Case Converter & Text Tools — Tylented",
    description: "7 free text tools: word counter with reading time, case converter (7 formats), remove duplicates, lorem ipsum, markdown preview, HTML encoder, HTML to Markdown.",
    url:         "https://arhat.info/tools/text",
    image:       "https://arhat.info/og-tools-text.png",
  }}
/>
      <div style={{ padding:'28px 0 48px', fontFamily:T.sans }}>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:5 }}>
            📝 Text Tools
          </h1>
          <p style={{ fontSize:13, color:T.muted }}>
            Word counter, case converter, lorem ipsum, markdown preview — all in your browser
          </p>
        </div>

        {/* Tab bar — matches DevTools style */}
        <div className="tt-tabs" style={{
          display:'flex', gap:4, borderBottom:`1px solid ${T.border}`,
          paddingBottom:0, marginBottom:0, overflowX:'auto',
        }}>
          {TOOLS.map(t => (
            <button key={t.id} className="tt-tab" onClick={() => {
              navigate(`/tools/text/${t.id}`);
              setActive(t.id)
            }} style={{
              padding:'10px 16px',
              border:'none',
              borderBottom:`2px solid ${active===t.id ? T.blue : 'transparent'}`,
              background:'transparent',
              color: active===t.id ? T.white : T.muted,
              fontSize:13, fontWeight: active===t.id ? 600 : 400,
              fontFamily:T.sans, cursor:'pointer',
              marginBottom:-1, transition:'color .15s',
              whiteSpace:'nowrap',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tool panel — matches DevTools card style */}
        {/* Tool panel */}
<div className="tt-panel" style={{
  background:T.bg2, border:`1px solid ${T.border}`,
  borderTop:'none', borderRadius:'0 0 12px 12px',
  padding:'20px 22px',
}}>
  <div style={{ marginBottom:16 }}>
    <h2 style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:3 }}>{meta?.label}</h2>
    <p style={{ fontSize:12, color:T.dim }}>{meta?.subtitle}</p>
  </div>
  <div className="tt-grid-2">
    {Tool && <Tool/>}
  </div>
</div>

{/* FAQ */}
<div style={{ marginTop:48 }}>
  <h2 style={{
    fontSize:18, fontWeight:800, color:T.white,
    letterSpacing:'-.02em', marginBottom:6,
  }}>
    Frequently Asked Questions
  </h2>
  <p style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
    Common questions about text utilities and formatting tools.
  </p>
  <TextFAQSection active={active}/>
</div>

      </div>
      </section>
      </div>
    </>
  );
}
